// Modal dialog (constitution §5.1, §5.7): focus moves into the dialog, Tab is trapped inside it,
// Escape closes it unless `busy`, and focus returns to what had it before.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import '../styles/ui.css';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

function trapTab(event: KeyboardEvent, root: HTMLElement): void {
  const items = focusables(root);
  const first = items[0];
  const last = items[items.length - 1];
  if (first === undefined || last === undefined) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !root.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

export interface ModalProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** While true (a request is running) Escape and the backdrop do not close the dialog. */
  readonly busy?: boolean;
  readonly role?: 'dialog' | 'alertdialog';
}

export function Modal(props: ModalProps): React.JSX.Element {
  const { onClose, busy = false } = props;
  const titleId = useId();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = box.current;
    if (root !== null) (focusables(root)[0] ?? root).focus();
    return () => before?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const root = box.current;
      if (root === null) return;
      if (event.key === 'Escape' && !busy) onClose();
      if (event.key === 'Tab') trapTab(event, root);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div className="modal-backdrop">
      <div
        ref={box}
        className="modal"
        role={props.role ?? 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="modal__title">
          {props.title}
        </h2>
        {props.children}
      </div>
    </div>
  );
}
